import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, GraduationCap, Landmark } from "lucide-react";
import { useNavigate } from "react-router-dom";
import FileUploader from "@/components/FileUploader";
import {
  STATE_NAMES,
  districtsByState,
  citiesOfDistrict,
} from "@/lib/indiaLocations";
import { sanitizeWebsiteInput } from "@/lib/instituteUrl";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyForm = {
  name: "",
  code: "",
  type: "",
  address1: "",
  address2: "",
  city: "",
  district: "",
  pincode: "",
  state: "",
  country: "India",
  latitude: "",
  longitude: "",
  year_of_establishment: "",
  approval_bodies: "",
  website_slug: "",
  email: "",
  contact_phone: "",
  whatsapp_phone: "",
  admission_phone: "",
  logo_url: "",
  logo_public_id: "",
};

function Field({ label, children, full, hint }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
      {hint && <div className="text-[11px] text-[#6b655b] mt-1.5 leading-snug">{hint}</div>}
    </label>
  );
}

export default function Institutes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    const [a, b] = await Promise.all([
      api.get("/institutes"),
      api.get("/masters/institute_type"),
    ]);
    setItems(a.data);
    setTypes(b.data);
  };

  useEffect(() => {
    (async () => {
      try {
        await fetchAll();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (inst) => {
    setEditing(inst);
    setForm({
      name: inst.name || "",
      code: inst.code || "",
      type: inst.type || "",
      address1: inst.address1 || "",
      address2: inst.address2 || "",
      city: inst.city || "",
      district: inst.district || "",
      pincode: inst.pincode || "",
      state: inst.state || "",
      country: inst.country || "India",
      latitude: inst.latitude || "",
      longitude: inst.longitude || "",
      year_of_establishment: inst.year_of_establishment ?? "",
      approval_bodies: inst.approval_bodies || "",
      website_slug: inst.website_slug || "",
      email: inst.email || "",
      contact_phone: inst.contact_phone || "",
      whatsapp_phone: inst.whatsapp_phone || "",
      admission_phone: inst.admission_phone || "",
      logo_url: inst.logo_url || "",
      logo_public_id: inst.logo_public_id || "",
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) {
      toast.error("Name and Code are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      year_of_establishment: form.year_of_establishment
        ? Number(form.year_of_establishment)
        : null,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/institutes/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Institute updated");
      } else {
        const { data } = await api.post("/institutes", payload);
        setItems((arr) => [data, ...arr]);
        toast.success("Institute created");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const inst = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/institutes/${inst.id}`);
      setItems((arr) => arr.filter((x) => x.id !== inst.id));
      toast.success("Institute deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="institutes-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">
            Institute Mapping
          </div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">
            Institutes under GS Mandal
          </h1>
          <p className="mt-2 text-sm text-[#6b655b]">
            {items.length} of 18 recorded. Manage the complete roster of institutes.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="institute-add-button"
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors"
        >
          <Plus size={14} strokeWidth={1.5} />
          Add Institute
        </button>
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
          data-testid="institutes-empty"
        >
          <GraduationCap
            size={32}
            strokeWidth={1.5}
            className="mx-auto text-[#234333]"
          />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">
            No institutes on record
          </div>
          <div className="mt-2 text-sm text-[#6b655b]">
            Start by adding the first institute under GS Mandal.
          </div>
          <button
            onClick={openCreate}
            data-testid="institute-empty-add-button"
            className="mt-6 inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors"
          >
            <Plus size={14} strokeWidth={1.5} />
            Add the first institute
          </button>
        </div>
      ) : (
        <div
          className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto"
          data-testid="institutes-table-wrapper"
        >
          <table className="w-full text-sm" data-testid="institutes-table">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                  Code
                </th>
                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                  Name
                </th>
                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                  Type
                </th>
                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                  City
                </th>
                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                  Year
                </th>
                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                  Approvals
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr
                  key={i.id}
                  className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                  data-testid={`institute-row-${i.id}`}
                >
                  <td className="px-4 py-4 font-mono text-[12px] text-[#1c1a17]">
                    {i.code}
                  </td>
                  <td className="px-4 py-4 text-[#1c1a17]">{i.name}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{i.type || "—"}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{i.city || "—"}</td>
                  <td className="px-4 py-4 text-[#6b655b]">
                    {i.year_of_establishment || "—"}
                  </td>
                  <td className="px-4 py-4 text-[#6b655b] max-w-[200px] truncate">
                    {i.approval_bodies || "—"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => navigate(`/admin/institutes/${i.id}/banks`)}
                      data-testid={`institute-banks-${i.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Landmark size={14} strokeWidth={1.5} /> Banks
                    </button>
                    <button
                      onClick={() => openEdit(i)}
                      data-testid={`institute-edit-${i.id}`}
                      className="ml-2 inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} /> Edit
                    </button>
                    <button
                      onClick={() => setConfirmDelete(i)}
                      data-testid={`institute-delete-${i.id}`}
                      className="ml-2 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                    >
                      <Trash2 size={14} strokeWidth={1.5} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="institute-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Institute" : "Add Institute"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Institute Name *">
              <input
                data-testid="institute-name-input"
                className={inputCls}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="Institute Code *">
              <input
                data-testid="institute-code-input"
                className={inputCls}
                value={form.code}
                onChange={(e) => set("code", e.target.value)}
              />
            </Field>
            <Field label="Type">
              <select
                data-testid="institute-type-select"
                className={inputCls}
                value={form.type}
                onChange={(e) => set("type", e.target.value)}
              >
                <option value="">— Select type —</option>
                {types.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Year of Establishment">
              <input
                data-testid="institute-year-input"
                type="number"
                className={inputCls}
                value={form.year_of_establishment}
                onChange={(e) => set("year_of_establishment", e.target.value)}
              />
            </Field>
            <Field label="Address Line 1" full>
              <input
                data-testid="institute-address1-input"
                className={inputCls}
                value={form.address1}
                onChange={(e) => set("address1", e.target.value)}
              />
            </Field>
            <Field label="Address Line 2" full>
              <input
                data-testid="institute-address2-input"
                className={inputCls}
                value={form.address2}
                onChange={(e) => set("address2", e.target.value)}
              />
            </Field>
            <Field label="State">
              <select
                data-testid="institute-state-input"
                className={inputCls}
                value={form.state}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    state: e.target.value,
                    district: "",
                    city: "",
                  }))
                }
              >
                <option value="">— Select state —</option>
                {STATE_NAMES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="District">
              <select
                data-testid="institute-district-input"
                className={inputCls}
                value={form.district}
                disabled={!form.state}
                onChange={(e) =>
                  setForm((f) => ({ ...f, district: e.target.value, city: "" }))
                }
              >
                <option value="">
                  {form.state ? "— Select district —" : "Select state first"}
                </option>
                {(districtsByState[form.state] || []).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="City">
              <select
                data-testid="institute-city-input"
                className={inputCls}
                value={form.city}
                disabled={!form.state}
                onChange={(e) => set("city", e.target.value)}
              >
                <option value="">
                  {form.state ? "— Select city —" : "Select state first"}
                </option>
                {citiesOfDistrict(form.state, form.district).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="PIN Code">
              <input
                data-testid="institute-pincode-input"
                className={inputCls}
                value={form.pincode}
                onChange={(e) => set("pincode", e.target.value)}
              />
            </Field>
            <Field label="Country">
              <input
                data-testid="institute-country-input"
                className={inputCls}
                value={form.country}
                onChange={(e) => set("country", e.target.value)}
              />
            </Field>
            <Field label="Latitude" hint="Decimal degrees, e.g., 19.876543">
              <input
                data-testid="institute-latitude-input"
                className={inputCls}
                placeholder="19.876543"
                inputMode="decimal"
                value={form.latitude}
                onChange={(e) => set("latitude", e.target.value)}
              />
            </Field>
            <Field label="Longitude" hint="Decimal degrees, e.g., 75.343315">
              <input
                data-testid="institute-longitude-input"
                className={inputCls}
                placeholder="75.343315"
                inputMode="decimal"
                value={form.longitude}
                onChange={(e) => set("longitude", e.target.value)}
              />
            </Field>
            <Field label="Approval Bodies (comma separated — AICTE / PCI / University)" full>
              <input
                data-testid="institute-approvals-input"
                className={inputCls}
                placeholder="e.g., AICTE, SPPU, DTE"
                value={form.approval_bodies}
                onChange={(e) => set("approval_bodies", e.target.value)}
              />
            </Field>
            <Field label="Public Website (slug OR full URL)" full>
              <input
                data-testid="institute-website-slug-input"
                className={inputCls}
                placeholder="e.g., polyaurangabad   or   https://www.mit.asia"
                value={form.website_slug}
                onChange={(e) => set("website_slug", sanitizeWebsiteInput(e.target.value))}
              />
              <div className="mt-1 text-[11px] text-[#6b655b] leading-snug">
                Enter a slug (lowercase letters / digits / hyphens) to host the CMS-driven public site at
                <code className="mx-1">/&lt;slug&gt;</code>, OR paste a full URL like
                <code className="mx-1">https://www.mit.asia</code> to link out to an existing external website.
              </div>
            </Field>
            <Field label="Email ID (public — header & footer)">
              <input
                data-testid="institute-email-input"
                type="email"
                className={inputCls}
                placeholder="principal@example.edu"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            <Field label="Contact Number (general office)">
              <input
                data-testid="institute-contact-phone-input"
                className={inputCls}
                placeholder="+91 240 2375333"
                value={form.contact_phone}
                onChange={(e) => set("contact_phone", e.target.value)}
              />
            </Field>
            <Field label="WhatsApp Contact Number (for Admission Enquiry FAB)">
              <input
                data-testid="institute-whatsapp-phone-input"
                className={inputCls}
                placeholder="919421078875 (no + or spaces — wa.me format)"
                value={form.whatsapp_phone}
                onChange={(e) => set("whatsapp_phone", e.target.value)}
              />
            </Field>
            <Field label="Admission Contact Number (shown on website header)">
              <input
                data-testid="institute-admission-phone-input"
                className={inputCls}
                placeholder="+91 9421 078 875"
                value={form.admission_phone}
                onChange={(e) => set("admission_phone", e.target.value)}
              />
            </Field>
            <Field label="Institute Logo (shown on the public website header & footer)" full>
              <FileUploader
                kind="inst_logo"
                label="Institute logo"
                url={form.logo_url}
                testid="institute-logo"
                onChange={(v) => {
                  setForm((f) => ({
                    ...f,
                    logo_url: v.url || "",
                    logo_public_id: v.public_id || "",
                  }));
                }}
              />
            </Field>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="institute-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="institute-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this institute?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.name} ({confirmDelete?.code}) will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel-button"
              className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="delete-confirm-button"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
